import {
  Box,
  Typography,
  Grid,
  Button,
  CircularProgress,
  useTheme,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import usePlanStore from 'store/planStore';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from 'pages/calculator/MediaPlanning/Plan/PlanSummary/styles';
import { AccordionSummaryContent } from 'pages/calculator/MediaPlanning/Plan/PlanSummary/AccordionSummaryContent/AccordionSummaryContent';
import DigitalBudgetTable from 'pages/calculator/MediaPlanning/common/BudgetTable/DigitalBudgetTable/DigitalBudgetTable';
import OfflineBudgetTable from 'pages/calculator/MediaPlanning/common/BudgetTable/OfflineBudgetTable/OfflineBudgetTable';
import { TotalBudgetSummary } from 'pages/calculator/MediaPlanning/Plan/PlanSummary/TotalBudgetSummary/TotalBudgetSummary';
import { groupCalculatedBudgetTouchpointData } from 'pages/calculator/MediaPlanning/common/BudgetTable/utils/groupTouchpointData';
import { useParams } from 'react-router-dom';
import { useEffect, useState } from 'react';
import useGenerateXls from './hooks/useGenerateXls';
import useSavePlan from './hooks/useSavePlan';
import useUpdatePlan from './hooks/useUpdatePlan';
import SavedPlanSnackbar from './SavedPlanSnackbar/SavedPlanSnackbar';
import getMissingMandatoryTouchpoints from './helpers/getMissingMandatoryTouchpoints';
import { useAuthContext } from '../../../../../context/AuthContext';
import BookingFlow from './BookingFlow/BookingFlow';
import SaveBookingFlow from './SaveBookingFlow/SaveBookingFlow';
import useMetaPredictionStatuses from './OverridingBookingFlow/hooks/useMetaPredictionStatuses';
import useBookMetaStart from './hooks/useBookMetaStart';
import getAbilityForReservation from './SaveBookingFlow/helpers/getAbilityForReservation';

function PlanSummary() {
  const [showSnackbar, setShowSnackbar] = useState(false);
  const [saveWithBookMetaOpen, setSaveWithBookMetaOpen] = useState(false);
  const [bookMetaOpen, setBookMetaOpen] = useState(false);
  const { t } = useTranslation();
  const {
    countryForms,
    selectedCountry,
    alreadySavedPlanId,
    storedCountryForms,
  } = usePlanStore();
  const { palette } = useTheme();
  const { generateXls } = useGenerateXls();
  const { userRoles } = useAuthContext();
  const hasAnyCalculatedBudget =
    Object.values(storedCountryForms).some(
      (form) => (form.calculatedBudget ?? []).length > 0
    ) ||
    Object.values(storedCountryForms).some(
      (form) => (form.offline?.tv?.offlineBudget ?? []).length > 0
    );

  const { id } = useParams();

  const {
    asyncSaveMediaPlan,
    isLoading: isSavingMediaPlan,
    data: savedPlanData,
  } = useSavePlan();
  const {
    asyncUpdateMediaPlan,
    isLoading: isUpdatingMediaPlan,
    data: updatedPlanData,
  } = useUpdatePlan(id);

  const { getMetaPredictionStatuses } = useMetaPredictionStatuses();

  const {
    asyncBookMetaStartTrigger,
    isLoading: isBookingLoading,
    error: bookingError,
    data: pollingResults,
    isPolling,
  } = useBookMetaStart(getMetaPredictionStatuses);

  const handleSavePlan = async () => {
    await asyncSaveMediaPlan();
  };

  const handleUpdatePlan = async (passedId: string | null) => {
    getMetaPredictionStatuses();
    await asyncUpdateMediaPlan(passedId);
  };

  useEffect(() => {
    if (savedPlanData || updatedPlanData) {
      setShowSnackbar(true);
    }
  }, [savedPlanData, updatedPlanData]);

  const handleCloseBookMeta = () => {
    setSaveWithBookMetaOpen(false);
    setBookMetaOpen(false);
  };

  const handleSavePlanOnly = () => {
    if (id || alreadySavedPlanId) {
      handleUpdatePlan(id ?? alreadySavedPlanId);
    } else {
      handleSavePlan();
    }

    setSaveWithBookMetaOpen(false);
  };

  const handleProceedBookMeta = async () => {
    if (id || alreadySavedPlanId) {
      await handleUpdatePlan(id ?? alreadySavedPlanId);
    } else {
      await asyncSaveMediaPlan();
    }

    const latestPlanId = usePlanStore.getState().alreadySavedPlanId;
    await asyncBookMetaStartTrigger({ planId: id || latestPlanId });
  };

  const getButtonContent = () => {
    if (isSavingMediaPlan || isUpdatingMediaPlan) {
      return (
        <CircularProgress
          size={16}
          sx={{
            color: palette.border.main,
          }}
        />
      );
    }
    return t('mediaPlanning.savePlan');
  };

  const isAnyMetaNotReserved = Object.values(countryForms).some(
    (country) => country.metaConfig?.reservationStatus !== 'reserved'
  );

  const planHasMetaRF = Object.values(countryForms).some((country) =>
    country.availableTouchpoints?.some(
      (touchpoint) =>
        touchpoint.touchpointFunnel === 'Reservation awareness (RF)'
    )
  );

  const handleClickSavePlan = () => {
    if (planHasMetaRF && isAnyMetaNotReserved) {
      setSaveWithBookMetaOpen(true);
    } else {
      handleSavePlanOnly();
    }
  };

  const handleClickBookMeta = async () => {
    if (planHasMetaRF) {
      if (id || alreadySavedPlanId) {
        await handleUpdatePlan(id ?? alreadySavedPlanId);
      } else {
        await asyncSaveMediaPlan();
      }

      const latestPlanId = usePlanStore.getState().alreadySavedPlanId;
      setSaveWithBookMetaOpen(false);
      setBookMetaOpen(true);
      await asyncBookMetaStartTrigger({
        planId: id || latestPlanId,
      });
    }
  };

  const missingMandatoryTouchpoints =
    getMissingMandatoryTouchpoints(storedCountryForms);

  const processedData = countryForms[
    selectedCountry!
  ]?.offline?.tv?.offlineBudget?.map((budget) => {
    if (budget.touchpoint !== 'outdoor') {
      return budget;
    }

    return {
      ...budget,
      children:
        budget.children?.filter((child) => child.isIncludedInCalculations) ||
        [],
    };
  });

  const processedDigitalData = storedCountryForms[
    selectedCountry!
  ]?.calculatedBudget?.filter((budget) => budget.isIncludedInCalculations);

  if (!Object.keys(countryForms).length || !hasAnyCalculatedBudget) {
    return (
      <Box
        sx={{
          my: '17px',
          p: '24px',
          display: 'flex',
          alignItems: 'center',
          borderRadius: '5px',
          backgroundColor: 'common.white',
        }}
      >
        <Typography>{t('mediaPlanning.noCalculatedBudget')}</Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        my: '17px',
        pb: '40px',
        display: 'flex',
        flexDirection: 'column',
        borderRadius: '5px',
        backgroundColor: 'common.white',
      }}
    >
      <Grid
        container
        sx={{
          p: '24px 15px',
        }}
      >
        <Grid item xs={12}>
          <Typography
            variant="body2"
            textTransform="uppercase"
            fontWeight={700}
            color="lorealPurple.main"
          >
            {t('mediaPlanning.planSummary')}
          </Typography>
        </Grid>
      </Grid>

      <TotalBudgetSummary />

      {Object.keys(storedCountryForms).map((country) => {
        return !processedDigitalData?.length &&
          !storedCountryForms[country].offline?.tv?.offlineBudget
            ?.length ? null : (
          <Accordion key={country}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <AccordionSummaryContent
                country={country}
                missingMandatoryTouchpoints={missingMandatoryTouchpoints}
              />
            </AccordionSummary>
            <AccordionDetails>
              <DigitalBudgetTable
                data={groupCalculatedBudgetTouchpointData(
                  processedDigitalData || []
                )}
              />
              <OfflineBudgetTable data={processedData || []} />
            </AccordionDetails>
          </Accordion>
        );
      })}

      <Grid
        container
        sx={{
          px: '24px',
          pt: '40px',
          borderTop: '1px solid',
          borderColor: 'border.main',
        }}
      >
        <Grid
          item
          xs={12}
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            columnGap: '24px',
          }}
        >
          {savedPlanData?.id || updatedPlanData?.id || id ? (
            <Button
              variant="text"
              sx={{ borderRadius: '30px', height: '34px' }}
              onClick={() => generateXls(id ?? alreadySavedPlanId ?? undefined)}
            >
              {t('mediaPlanning.downloadXls')}
            </Button>
          ) : null}
          {isAnyMetaNotReserved &&
            planHasMetaRF &&
            getAbilityForReservation(userRoles) && (
              <Button
                disabled={!!Object.keys(missingMandatoryTouchpoints).length}
                onClick={handleClickBookMeta}
                variant="outlined"
                sx={{ borderRadius: '30px', height: '34px', minWidth: '153px' }}
              >
                {t('mediaPlanning.bookMeta')}
              </Button>
            )}
          <Button
            disabled={!!Object.keys(missingMandatoryTouchpoints).length}
            onClick={handleClickSavePlan}
            variant="contained"
            sx={{ borderRadius: '30px', height: '34px', minWidth: '153px' }}
          >
            {getButtonContent()}
          </Button>
        </Grid>
      </Grid>
      <SavedPlanSnackbar
        showSnackbar={showSnackbar}
        setShowSnackbar={setShowSnackbar}
      />
      <SaveBookingFlow
        open={saveWithBookMetaOpen}
        onClose={handleCloseBookMeta}
        onSuccess={handleSavePlanOnly}
        onSecondaryAction={handleClickBookMeta}
        header=""
      />
      <BookingFlow
        open={bookMetaOpen}
        onClose={handleCloseBookMeta}
        onSuccess={() => setBookMetaOpen(false)}
        header=""
        isLoading={
          isBookingLoading ||
          isPolling ||
          pollingResults?.data.jobStatus === 'processing' ||
          pollingResults?.data.jobStatus === 'initiated'
        }
        error={bookingError}
        successData={pollingResults?.data.results}
      />
    </Box>
  );
}

export default PlanSummary;
